import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DateInput } from "../../components/ui/DateInput";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { AppointmentAPI } from "../../services/appointment.service";
import type { AppointmentListItem, AppointmentSlot } from "../../services/appointment.service";
import { DoctorAPI } from "../../services/doctor.service";
import type { DoctorListItem } from "../../services/doctor.service";
import { PatientAPI } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import { AppointmentCard } from "../../components/appointments/AppointmentCard";
import { BookAppointmentModal } from "../../components/appointments/BookAppointmentModal";
import { subscribe } from "../../services/sync-push.engine";
import { getSessionRole } from "../../components/RequireAuth";
import { db } from "../../db/schema";
import { unwrapFromStorage } from "../../db/encryption-bridge";
import type { AppointmentRecord } from "../../db/db.types";
import { isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import { AccessDenied } from "../../components/RequirePermission";
import { useRole } from "../../hooks/useRole";
import { OnboardingAPI } from "../../services/onboarding.service";

// ── Helpers ───────────────────────────────────────────────────────────────────
const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
// Parse YYYY-MM-DD as UTC midnight so the API receives the date the user selected
const toISOParam = (s: string) => new Date(s).toISOString();

// ── Mini Calendar ─────────────────────────────────────────────────────────────
const MiniCalendar: React.FC<{
  selected: string;
  onSelect: (d: string) => void;
}> = ({ selected, onSelect }) => {
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date(selected + "T00:00:00");
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const today = toDateStr(new Date());

  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 select-none">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setViewDate(new Date(year, month - 1, 1))}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition text-base leading-none"
        >
          ‹
        </button>
        <span className="text-sm font-semibold text-slate-700">
          {viewDate.toLocaleString("default", { month: "long" })} {year}
        </span>
        <button
          onClick={() => setViewDate(new Date(year, month + 1, 1))}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition text-base leading-none"
        >
          ›
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 mb-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="text-center text-[10px] font-medium text-slate-400 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((day, i) => {
          if (!day) return <div key={i} />;
          const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
          const isSel = dateStr === selected;
          const isTod = dateStr === today;
          return (
            <button
              key={i}
              onClick={() => onSelect(dateStr)}
              className={`h-7 w-7 mx-auto flex items-center justify-center rounded-full text-xs transition font-medium ${
                isSel
                  ? "bg-cyan-500 text-white shadow-sm"
                  : isTod
                  ? "bg-cyan-50 text-cyan-600 ring-1 ring-cyan-300"
                  : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Stat chip ─────────────────────────────────────────────────────────────────
const StatChip: React.FC<{ label: string; value: number; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border ${color} bg-white shadow-sm`}>
    <span className="text-lg font-bold">{value}</span>
    <span className="text-xs font-medium opacity-80">{label}</span>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────
type Tab = "today" | "upcoming" | "past";

// Backend roles are capitalised ("Admin"/"Doctor"/"Receptionist"); the card
// expects the lowercase union. Unknown roles fall back to undefined (no
// role-specific actions shown).
const normalizeRole = (role: string | null): "receptionist" | "doctor" | "admin" | undefined => {
  switch (role) {
    case "Admin":        return "admin";
    case "Doctor":       return "doctor";
    case "Receptionist": return "receptionist";
    default:             return undefined;
  }
};

const AppointmentsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDoctor, canOr } = useRole();
  // This page is reachable with appointments.view alone (App.tsx). Booking and
  // changing appointments are separate grants, so their buttons follow them —
  // otherwise a View Appointments-only role sees Book / Bulk reschedule and
  // gets a 403 on submit.
  const canBookAppointment = canOr("appointments.create", true);
  const canManageAppointments = canOr("appointments.manage", true);
  const userRole = useMemo(() => normalizeRole(getSessionRole()), []);

  const todayStr = toDateStr(new Date());
  const [date, setDate] = useState(searchParams.get("date") ?? todayStr);
  const setDateAndResetTab = useCallback((d: string) => {
    setDate(d);
    // Selecting a specific date returns to the neutral "today" view (all
    // statuses for that date). The "Today" tab only lights up when the chosen
    // date actually is today (see the active-state calc in the tab bar below).
    setTab("today");
  }, []);
  const [doctorFilter, setDoctorFilter] = useState(searchParams.get("doctor_id") ?? "");
  const [tab, setTab] = useState<Tab>("today");
  const [search, setSearch] = useState("");

  // "Past" is relative to the calendar selection — it shows the day *before*
  // whichever date is selected. When that selection is a future date, "the day
  // before it" still isn't in the past, so the Past tab is hidden entirely.
  const prevDayStr = toDateStr(new Date(new Date(date).getTime() - 86_400_000));
  const isFutureDate = date > todayStr;
  // Symmetrically, "Upcoming" (the selected date onward) is meaningless once the
  // selection is a past date, so that tab is hidden when a past date is picked.
  const isPastDate = date < todayStr;
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  // Server refused the read (403); holds its reason. See the catch in fetchAppointments.
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [patients, setPatients] = useState<PatientDbRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [isBookOpen, setIsBookOpen] = useState(false);
  // Mobile/tablet-only: the mini calendar / Quick Jump / Filter by Doctor panel
  // is a static lg:+ sidebar on desktop, but has no visible entry point below
  // lg: — this drives its off-canvas drawer on smaller screens.
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // A doctor's own id — used to scope the offline fallback to their own
  // appointments (the online list is already doctor-scoped by the backend).
  const [myDoctorId, setMyDoctorId] = useState<string | null>(null);
  const [prefillPatientId] = useState(() => searchParams.get("patient_id") ?? "");
  // Set when opened from a notification link (?appointment_id=...) — scrolls to
  // and highlights that card once the list loads, regardless of current tab/date.
  const [highlightAppointmentId] = useState(() => searchParams.get("appointment_id") ?? "");

  // Auto-open booking modal when navigating from PatientPage
  useEffect(() => {
    // Deep link from the patient profile — honour it only with the grant.
    if (searchParams.get("open_booking") === "true" && canBookAppointment) {
      setIsBookOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL params
  useEffect(() => {
    const p: Record<string, string> = { date };
    if (doctorFilter) p.doctor_id = doctorFilter;
    setSearchParams(p, { replace: true });
  }, [date, doctorFilter, setSearchParams]);

  useEffect(() => {
    DoctorAPI.list({ is_active: true }).then(setDoctors).catch(() => {});
    PatientAPI.getAll().then(setPatients).catch(() => {});
  }, []);

  // Resolve the signed-in doctor's own doctor_id (for offline scoping).
  useEffect(() => {
    if (!isDoctor) return;
    let cancelled = false;
    OnboardingAPI.getMe()
      .then((me) => {
        if (!cancelled && me.role === "Doctor" && me.doctor_id) {
          setMyDoctorId(me.doctor_id);
        }
      })
      .catch(() => { /* online path is backend-scoped; offline stays fail-closed */ });
    return () => { cancelled = true; };
  }, [isDoctor]);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    // The three tabs are time-relative views over the selected calendar date:
    //   - "today"    -> the selected date (every status)
    //   - "past"     -> the day BEFORE the selected date (every status:
    //                   completed, cancelled, no_show, in_progress, anything)
    //   - "upcoming" -> not-yet-happened appointments (scheduled/confirmed)
    //                   from the selected date onward
    const dateFilter =
      tab === "past"
        ? { date: toISOParam(prevDayStr) }
        : tab === "upcoming"
        ? { start_date: toISOParam(date) }
        : { date: toISOParam(date) };
    // "Upcoming" excludes in_progress (already being seen) and the terminal
    // states (completed/cancelled/no_show) — only what is still to come.
    const statusFilter = tab === "upcoming" ? { statuses: "requested,approved,scheduled,confirmed" } : {};
    try {
      const [data, slotData] = await Promise.all([
        AppointmentAPI.list({
          ...dateFilter,
          ...statusFilter,
          ...(doctorFilter ? { doctor_id: doctorFilter } : {}),
        }),
        AppointmentAPI.listSlots({ ...dateFilter }),
      ]);
      setAppointments(data);
      setSlots(slotData);
      setAccessDenied(null);
    } catch (e: any) {
      // `appointments.view` was revoked mid-session (the route guard only runs on
      // entry). Clear what is already on screen — a toast alone would leave the
      // previous fetch's appointments rendered behind it — and stay ahead of the
      // offline branch so cached rows are not shown under an offline message.
      if (isPermissionDenied(e)) {
        setAppointments([]);
        setSlots([]);
        setAccessDenied(e instanceof Error ? e.message : null);
        return;
      }
      setAccessDenied(null);

      if (isBackendUnreachable(e)) {
        // Offline fallback — read from Dexie. appointment_date is a plaintext
        // indexed field so we can filter without decrypting. Matched at day
        // granularity (robust to time-format differences in the cached value).
        try {
          const inRange = (apptDate?: string) => {
            if (!apptDate) return false;
            const day = apptDate.slice(0, 10);
            if (tab === "past")     return day === prevDayStr;
            if (tab === "upcoming") return day >= date;
            return day === toISOParam(date).slice(0, 10);
          };
          const raw = await db.appointments
            .filter(r => {
              // Match the selected tab's day range (today / prev day / upcoming).
              if (!inRange(r.appointment_date)) return false;
              // Doctors are restricted to their own appointments; fail closed
              // until myDoctorId resolves so other doctors' cached rows never show.
              if (isDoctor) return r.doctor_id === myDoctorId;
              return !doctorFilter || r.doctor_id === doctorFilter;
            })
            .toArray();
          const decrypted = await Promise.all(
            raw.map(r => unwrapFromStorage<AppointmentRecord>(r as unknown as Record<string, unknown>))
          );
          const mapped: AppointmentListItem[] = decrypted.map(r => ({
            appointment_id:   r.appointment_id,
            patient_id:       r.patient_id,
            doctor_id:        r.doctor_id,
            slot_id:          (r as any).slot_id ?? null,
            source:           (r as any).source ?? "scheduled", // pre-walk-in cached rows have no source
            status:           r.status.toLowerCase() as AppointmentListItem["status"],
            notes:            (r as any).notes ?? null,
            cancellation_reason: (r as any).cancellation_reason ?? null,
            rejection_reason: (r as any).rejection_reason ?? null,
            rescheduled_to_id: (r as any).rescheduled_to_id ?? null,
            arrived_at:       (r as any).arrived_at ?? null,
            visit_id:         (r as any).visit_id ?? null,
            booked_by:        (r as any).booked_by ?? null,
            booking_source:   (r as any).booking_source ?? null,
            created_at:       r.local_updated_at,
          }));
          setAppointments(
            tab === "upcoming"
              ? mapped.filter(a => a.status === "requested" || a.status === "approved" || a.status === "scheduled" || a.status === "confirmed")
              : mapped
          );
          setSlots([]);
        } catch {
          toast.error("You are offline and no cached appointments found");
        }
      } else {
        toast.error(e.message || "Failed to load appointments");
      }
    } finally {
      setLoading(false);
    }
  }, [date, doctorFilter, isDoctor, myDoctorId, tab, prevDayStr]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Re-fetch when the background push engine syncs any record — an offline
  // appointment that just pushed should appear immediately without a manual refresh.
  // Debounce 1 s so we don't fire during the brief network-transition window
  // right after the 'online' event, which would cause unnecessary error toasts.
  useEffect(() => {
    let tid: ReturnType<typeof setTimeout>;
    const unsub = subscribe(() => {
      if (!navigator.onLine) return;
      clearTimeout(tid);
      tid = setTimeout(fetchAppointments, 1000);
    });
    return () => { unsub(); clearTimeout(tid); };
  }, [fetchAppointments]);

  const doctorNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    doctors.forEach((d) => { m[d.doctor_id] = d.name; });
    return m;
  }, [doctors]);

  const patientMap = useMemo(() => {
    const m: Record<string, { name: string; display_id?: string }> = {};
    patients.forEach((p) => {
      if (p.patient_id) m[p.patient_id] = { name: p.name ?? `${p.first_name} ${p.last_name}`, display_id: p.display_id };
    });
    return m;
  }, [patients]);

  const slotMap = useMemo(() => {
    const m: Record<string, AppointmentSlot> = {};
    slots.forEach((s) => { m[s.slot_id] = s; });
    return m;
  }, [slots]);

  // Stats
  const stats = useMemo(() => {
    const s = { scheduled: 0, confirmed: 0, in_progress: 0, completed: 0, cancelled: 0, no_show: 0 };
    appointments.forEach((a) => { (s as any)[a.status] = ((s as any)[a.status] ?? 0) + 1; });
    return s;
  }, [appointments]);

  // The date range is resolved server-side per tab (see fetchAppointments), so
  // every status is shown — Past in particular lists ALL of the previous day's
  // appointments regardless of status. Here we only narrow by the search box.
  const filtered = useMemo(() => {
    let list = appointments;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((a) =>
        (doctorNameMap[a.doctor_id] ?? "").toLowerCase().includes(q) ||
        (a.notes ?? "").toLowerCase().includes(q) ||
        a.status.includes(q)
      );
    }
    return list;
  }, [appointments, search, doctorNameMap]);

  const TABS: { id: Tab; label: string }[] = [
    { id: "today",    label: "Today" },
    { id: "upcoming", label: "Upcoming" },
    { id: "past",     label: "Past" },
  ];

  if (accessDenied) {
    return (
      <div className="flex h-screen bg-[#f7f8fc] overflow-hidden">
        <Sidebar />
        <div className="flex-1 min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
          <AppHeader title="Appointments" />
          <AccessDenied label="Appointments" detail={accessDenied} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#f7f8fc]">
      <Sidebar />

      <div className="flex-1 min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Appointments" searchMode="both" />
        {/* ── Page header ──────────────────────────────────────────── */}
        <div className="px-6 pt-6 pb-0">
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Appointments</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                {appointments.length} appointment{appointments.length !== 1 ? "s" : ""} ·{" "}
                {tab === "past"
                  ? (date === todayStr ? "Yesterday" : prevDayStr)
                  : tab === "upcoming"
                  ? "Upcoming"
                  : date === todayStr
                  ? "Today"
                  : date}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate("/appointments/queue")}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-white transition shadow-sm bg-white"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Queue
              </button>
              <button
                onClick={() => navigate("/appointments/calendar")}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-white transition shadow-sm bg-white"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Calendar
              </button>
              {canBookAppointment && (
                <button
                  onClick={() => setIsBookOpen(true)}
                  className="flex items-center gap-1.5 bg-cyan-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-cyan-600 transition shadow-sm"
                >
                  <span className="text-base leading-none font-bold">+</span>
                  Book Appointment
                </button>
              )}
            </div>
          </div>

          {/* Stats bar */}
          {appointments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              <StatChip label="Scheduled"   value={stats.scheduled}   color="text-blue-600 border-blue-100" />
              <StatChip label="Confirmed"   value={stats.confirmed}   color="text-cyan-600 border-cyan-100" />
              <StatChip label="In Progress" value={stats.in_progress} color="text-amber-600 border-amber-100" />
              <StatChip label="Completed"   value={stats.completed}   color="text-green-600 border-green-100" />
              {stats.cancelled > 0 && (
                <StatChip label="Cancelled" value={stats.cancelled} color="text-slate-500 border-slate-200" />
              )}
              {stats.no_show > 0 && (
                <StatChip label="No Show" value={stats.no_show} color="text-red-500 border-red-100" />
              )}
            </div>
          )}
        </div>

        {/* ── Two-column layout ────────────────────────────────────── */}
        <div className="px-6 pb-6 flex gap-5 items-start">
          {/* Left: mini calendar + doctor filter — a static lg:+ sidebar; below
              lg: it becomes an off-canvas drawer opened via the "Filters"
              button next to the search bar (same pattern as the Calendar
              page's mobile stats panel). */}
          {showFilterPanel && (
            <div
              className="lg:hidden fixed inset-0 z-30 bg-black/20"
              onClick={() => setShowFilterPanel(false)}
            />
          )}
          <div
            className={`${showFilterPanel ? "fixed inset-y-0 left-0 z-40 block" : "hidden"} lg:static lg:z-auto lg:block w-64 lg:w-56 flex-shrink-0 space-y-4 bg-[#f7f8fc] lg:bg-transparent overflow-y-auto p-4 lg:p-0 shadow-xl lg:shadow-none`}
          >
            <div className="lg:hidden flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-slate-800">Filters</p>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600"
                aria-label="Close filters"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <MiniCalendar
              selected={date}
              onSelect={(d) => { setDateAndResetTab(d); setShowFilterPanel(false); }}
            />

            {/* Quick navigation */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 space-y-1">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">
                Quick Jump
              </p>
              {[
                { label: "Yesterday",  delta: -1 },
                { label: "Today",      delta:  0 },
                { label: "Tomorrow",   delta:  1 },
              ].map(({ label, delta }) => {
                const d = new Date();
                d.setDate(d.getDate() + delta);
                const ds = toDateStr(d);
                return (
                  <button
                    key={label}
                    onClick={() => { setDateAndResetTab(ds); setShowFilterPanel(false); }}
                    className={`w-full text-left px-3 min-h-[44px] lg:min-h-0 lg:py-1.5 rounded-xl text-xs transition font-medium ${
                      date === ds
                        ? "bg-cyan-50 text-cyan-700"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Doctor filter — hidden for doctors, who are scoped to their own
                appointments server-side, so filtering by doctor is moot. */}
            {!isDoctor && doctors.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">
                  Filter by Doctor
                </p>
                <div className="space-y-1">
                  <button
                    onClick={() => { setDoctorFilter(""); setShowFilterPanel(false); }}
                    className={`w-full text-left px-3 min-h-[44px] lg:min-h-0 lg:py-1.5 rounded-xl text-xs font-medium transition ${
                      !doctorFilter ? "bg-cyan-50 text-cyan-700" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    All Doctors
                  </button>
                  {doctors.map((d) => (
                    <button
                      key={d.doctor_id}
                      onClick={() => { setDoctorFilter(d.doctor_id); setShowFilterPanel(false); }}
                      className={`w-full text-left px-3 min-h-[44px] lg:min-h-0 lg:py-1.5 rounded-xl text-xs font-medium transition truncate ${
                        doctorFilter === d.doctor_id
                          ? "bg-cyan-50 text-cyan-700"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
                {doctorFilter && canManageAppointments && (
                  <button
                    onClick={() => navigate(
                      `/appointments/bulk-reschedule?source=sick_day&doctor_id=${doctorFilter}&date=${encodeURIComponent(toISOParam(date))}`
                    )}
                    className="w-full mt-2 text-left px-3 py-2 rounded-xl text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition border-t border-slate-100"
                  >
                    Doctor called in sick? Bulk reschedule this day
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right: search + tabs + list */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Search */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by doctor, notes, or status…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-base sm:text-sm bg-white text-slate-700 outline-none focus:ring-2 focus:ring-cyan-300 shadow-sm"
                />
              </div>
              {/* Mobile date picker */}
              <DateInput
                value={date}
                onChange={(e) => setDateAndResetTab(e.target.value)}
                className="lg:hidden text-sm border border-slate-200 rounded-xl px-3 py-2.5 text-slate-600 outline-none bg-white shadow-sm"
              />
              {/* Mobile/tablet: opens the mini calendar / Quick Jump / Filter by
                  Doctor drawer — that panel is a static lg:+ sidebar with no
                  other entry point below lg:. */}
              <button
                onClick={() => setShowFilterPanel(true)}
                className="lg:hidden flex items-center justify-center min-w-[44px] min-h-[44px] rounded-xl border border-slate-200 text-slate-600 bg-white shadow-sm"
                aria-label="Filters"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 9h12M9 15h6" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white border border-slate-100 rounded-xl p-1 shadow-sm w-fit">
              {/* "Past" = the day before the selected date — hidden for a future
                  selection (that day still isn't in the past). "Upcoming" = the
                  selected date onward — hidden for a past selection. */}
              {TABS.filter((t) =>
                !(t.id === "past" && isFutureDate) &&
                !(t.id === "upcoming" && isPastDate)
              ).map((t) => {
                // `tab` is the single source of truth for which filter is active,
                // so exactly one tab can ever be highlighted. "Today" additionally
                // requires the selected date to be today — otherwise the user has
                // navigated to a specific past/future date via the calendar and no
                // time filter applies.
                const active =
                  t.id === "today"
                    ? tab === "today" && date === todayStr
                    : tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      if (t.id === "today") {
                        setDate(todayStr);
                        setTab("today");
                      } else {
                        setTab(t.id);
                      }
                    }}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                      active
                        ? "bg-cyan-500 text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* List */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
                <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">
                  {search
                    ? "No appointments match your search."
                    : tab === "past"
                    ? `No appointments for ${date === todayStr ? "yesterday" : prevDayStr}.`
                    : tab === "upcoming"
                    ? "No upcoming appointments."
                    : `No appointments for ${date === todayStr ? "today" : date}.`}
                </p>
                {tab === "today" && !search && !isPastDate && canBookAppointment && (
                  <button
                    onClick={() => setIsBookOpen(true)}
                    className="text-sm text-cyan-600 hover:underline"
                  >
                    + Book one now
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((appt) => (
                  <AppointmentCard
                    key={appt.appointment_id}
                    appointment={{
                      ...appt,
                      doctor_name: doctorNameMap[appt.doctor_id] ?? undefined,
                      patient_name: patientMap[appt.patient_id]?.name,
                      patient_display_id: patientMap[appt.patient_id]?.display_id,
                      slot_start: appt.slot_id ? slotMap[appt.slot_id]?.start_time : undefined,
                      slot_end: appt.slot_id ? slotMap[appt.slot_id]?.end_time : undefined,
                    }}
                    userRole={userRole}
                    onRefresh={fetchAppointments}
                    highlighted={!!highlightAppointmentId && appt.appointment_id === highlightAppointmentId}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <BookAppointmentModal
        isOpen={isBookOpen}
        onClose={() => setIsBookOpen(false)}
        onSuccess={fetchAppointments}
        prefillDate={date}
        prefillDoctorId={doctorFilter}
        prefillPatientId={prefillPatientId}
      />
    </div>
  );
};

export default AppointmentsPage;
